import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { BookOpen, TrendingUp, MapPin } from "lucide-react";

import { DollarSign, FileText, Building2, Wrench, Server, PenTool } from "lucide-react";

const Resources = () => {
  const serviceGuides = [
    {
      title: "Lawn Care Web Design Agency",
      path: "/lawn-care-web-design-agency/",
      description: "How to choose a web design agency that specializes in lawn care businesses.",
      icon: Building2,
    },
    {
      title: "Lawn Care Website Maintenance",
      path: "/lawn-care-website-maintenance/",
      description: "What ongoing maintenance your lawn care website needs to stay secure and fast.",
      icon: Wrench,
    },
    {
      title: "Lawn Care Website Hosting",
      path: "/lawn-care-website-hosting/",
      description: "Hosting options for lawn care websites—speed, uptime, and what matters.",
      icon: Server,
    },
  ];

  const articles = [
    {
      title: "Lawn Care Website Checklist",
      slug: "lawn-care-website-checklist",
      description: "The essential features and pages every lawn care website needs to convert visitors into customers.",
      icon: BookOpen,
    },
    {
      title: "How to Get More Lawn Care Leads Online",
      slug: "how-to-get-more-lawn-care-leads-online",
      description: "Proven strategies to attract more qualified leads and grow your lawn care business through your website.",
      icon: TrendingUp,
    },
    {
      title: "Google Business Profile for Lawn Care",
      slug: "google-business-profile-for-lawn-care",
      description: "Step-by-step guide to optimizing your Google Business Profile to show up in local searches and get more calls.",
      icon: MapPin,
    },
  ];

  const guides = [
    {
      title: "Lawn Care Website Cost",
      path: "/lawn-care-website-cost/",
      description: "Compare DIY, freelancer, agency, and subscription pricing. Real numbers for lawn care business owners.",
      icon: DollarSign,
    },
    {
      title: "Landscaping Website Cost",
      path: "/landscaping-website-cost/",
      description: "Pricing guide for landscapers and hardscapers—from patios to retaining walls.",
      icon: DollarSign,
    },
    {
      title: "Lawn Care Quote Form Best Practices",
      path: "/lawn-care-quote-form/",
      description: "Build a 4-field form that converts visitors into leads with smart placement and spam control.",
      icon: FileText,
    },
    {
      title: "Landscaping Quote Form Best Practices",
      path: "/landscaping-quote-form/",
      description: "Capture leads for design, hardscape, and outdoor living projects with the right form setup.",
      icon: FileText,
    },
  ];

  const advancedGuides = [
    {
      title: "Lawn Care Website Design Pricing",
      path: "/lawn-care-website-design-pricing/",
      description: "Understand monthly costs, what's included, and how subscription pricing works.",
      icon: DollarSign,
    },
    {
      title: "Landscaping Website Design Pricing",
      path: "/landscaping-website-design-pricing/",
      description: "Pricing clarity for landscapers and hardscapers considering a professional site.",
      icon: DollarSign,
    },
    {
      title: "Lawn Care Website Redesign",
      path: "/lawn-care-website-redesign/",
      description: "When to redesign your site, what to fix, and how to improve conversions.",
      icon: TrendingUp,
    },
    {
      title: "Landscaping Portfolio Website",
      path: "/landscaping-portfolio-website/",
      description: "Showcase your best projects to close more high-ticket landscaping jobs.",
      icon: BookOpen,
    },
    {
      title: "Lawn Care Website SEO Services",
      path: "/lawn-care-website-seo-services/",
      description: "On-page SEO structure to rank your service pages and drive local leads.",
      icon: TrendingUp,
    },
  ];

  return (
    <>
      <SEO 
        title="Lawn Care & Landscaping Website Design Guides"
        description="Free lawn care website design guides and landscaping website design resources. Learn how to build a website that gets clients, improve your web design, and rank locally."
        keywords="lawn care website design guides, landscaping website design resources, lawn care website examples, landscaping website pricing"
        canonical="https://www.leafrushmarketing.com/resources/"
        ogUrl="https://www.leafrushmarketing.com/resources/"
      />
      <div className="flex flex-col">
        {/* Hero */}
        <section className="py-20 md:py-32">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-4xl text-center space-y-6">
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
                Lawn Care & Landscaping <span className="text-primary">Website Design</span> Guides
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Practical guides to help you build a better lawn care website design or landscaping website design—from pricing and examples to SEO and conversions.
              </p>
              <p className="text-muted-foreground">
                Browse our{" "}
                <NavLink to="/lawn-care-website-examples/" className="text-primary hover:underline">lawn care website examples</NavLink>,{" "}
                <NavLink to="/landscaping-website-examples/" className="text-primary hover:underline">landscaping website examples</NavLink>, and{" "}
                <NavLink to="/websites/" className="text-primary hover:underline">website design pricing</NavLink>.
              </p>
            </div>
          </div>
        </section>

        {/* Service Guides Grid (NEW) */}
        <section className="py-12 md:py-20 bg-primary/5">
          <div className="container px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">Website Design Service Guides</h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {serviceGuides.map((guide) => {
                const Icon = guide.icon;
                return (
                  <Card key={guide.path} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <CardTitle className="text-xl">{guide.title}</CardTitle>
                      <CardDescription className="text-base">
                        {guide.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <NavLink to={guide.path}>
                        <Button variant="outline" className="w-full">
                          Read Guide
                        </Button>
                      </NavLink>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-12 md:py-20">
          <div className="container px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">Resource Articles</h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {articles.map((article) => {
                const Icon = article.icon;
                return (
                  <Card key={article.slug} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <CardTitle className="text-xl">{article.title}</CardTitle>
                      <CardDescription className="text-base">
                        {article.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <NavLink to={`/resources/${article.slug}`}>
                        <Button variant="outline" className="w-full">
                          Read Guide
                        </Button>
                      </NavLink>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Website Guides Grid */}
        <section className="py-12 md:py-20 bg-accent/20">
          <div className="container px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">Website & Pricing Guides</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {guides.map((guide) => {
                const Icon = guide.icon;
                return (
                  <Card key={guide.path} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <CardTitle className="text-lg">{guide.title}</CardTitle>
                      <CardDescription className="text-sm">
                        {guide.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <NavLink to={guide.path}>
                        <Button variant="outline" className="w-full">
                          Read Guide
                        </Button>
                      </NavLink>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Advanced Guides Grid */}
        <section className="py-12 md:py-20">
          <div className="container px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">Advanced Guides</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {advancedGuides.map((guide) => {
                const Icon = guide.icon;
                return (
                  <Card key={guide.path} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <CardTitle className="text-lg">{guide.title}</CardTitle>
                      <CardDescription className="text-sm">
                        {guide.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <NavLink to={guide.path}>
                        <Button variant="outline" className="w-full">
                          Read Guide
                        </Button>
                      </NavLink>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-20 bg-secondary/30">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">
                Need Help Implementing These Strategies?
              </h2>
              <p className="text-lg text-muted-foreground">
                We build high-converting websites exclusively for lawn care and landscaping businesses. 
                Let's discuss how we can help you grow your online presence.
              </p>
              <NavLink to="/contact#get-started">
                <Button size="lg" className="text-lg px-8">
                  Get Started
                </Button>
              </NavLink>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Resources;
